
// AUTO-GENERATED SDK for BookingPartnerChange
import { Injectable } from '@angular/core';
import { GraphqlApiService } from '../../api/graphql-api.service';
import { BookingPartnerChangeBuilder } from './booking-partner-change.builder';
import { BookingPartnerChangeSql } from './booking-partner-change.sqlbuilder';
import type { ApiResponse } from '../../api/types';
import type { BookingPartnerChangePoco } from '@libs/graphql/models';

@Injectable({ providedIn:'root' })
export class BookingPartnerChangeSdk {

  constructor(private api: GraphqlApiService) {}

  // ============================================================
  // SAFE JSON
  // ============================================================
  private safeJSON(v: any) {
    try { return JSON.stringify(v); }
    catch { return '{}'; }
  }

  // ============================================================
  // SAFE RESPONSE WRAPPER
  // ============================================================
  private wrap<T>(raw: any, key: string): ApiResponse<T> {
    const node = raw[key] ?? {};
    return {
      success: node.success ?? false,
      message: node.message ?? '',
      error: node.error ?? '',
      totalRecord: node.totalRecord ?? 0,
      data: (node.data ?? []) as T
    };
  }

  // ============================================================
  // SAFE SELECT
  // ============================================================
  private safeSelect(fields: string[]) {
    return fields.length
      ? fields.map(f => ({ columnName: f, alias: f, isTrusted: true }))
      : [{ columnName: '*', alias: '*', isTrusted: true }];
  }

  // ============================================================
  // FILTER BUILDER
  // ============================================================
  private cf(f: any): any[] {
    if (!f || typeof f !== 'object') return [];

    if (f.AND)
      return [{
        logic: 'And',
        isTrusted: false,
        subFilters: f.AND.map((x: any) => this.cf(x)[0])
      }];

    if (f.OR)
      return [{
        logic: 'Or',
        isTrusted: false,
        subFilters: f.OR.map((x: any) => this.cf(x)[0])
      }];

    const opMap: any = {
      eq: '=', ne: '<>', like: 'LIKE',
      gt: '>', gte: '>=', lt: '<', lte: '<=',
      in: 'IN', notIn: 'NOT IN'
    };

    const rows: any[] = [];

    for (const k of Object.keys(f)) {
      const v = f[k];

      if (typeof v !== 'object') {
        rows.push({
          column: k,
          operator: '=',
          value: v,
          logic: 'And',
          subFilters: [],
          isTrusted: false
        });
      } else {
        const op = Object.keys(v)[0];
        rows.push({
          column: k,
          operator: opMap[op] ?? op,
          value: v[op],
          logic: 'And',
          subFilters: [],
          isTrusted: false
        });
      }
    }

    return rows;
  }

  // ============================================================
  // LIST MODE
  // ============================================================
  async list(
    filter: any = {},
    sorts: any[] = [],
    fields: string[] = [],
    page = 1,
    pageSize = 50,
    options: { strict?: boolean, useCache?: boolean, debug?: boolean } = {}
  ): Promise<ApiResponse<BookingPartnerChangePoco[]>> {

    const gql = `query ListBookingPartnerChange($input: DynamicQueryJsonInput) {
      listBookingPartnerChange(input: $input) {
        success
        message
        error
        totalRecord
        data {
          ${fields.length ? fields.join('\n          ') : 'id'}
        }
      }
    }`;

    const where = this.cf(filter);
    const selects = this.safeSelect(fields);
    const safeSorts = sorts && sorts.length
      ? sorts
      : [{ column: 'id', desc: true, isTrusted: true }];

    const raw = await this.api.request<any>(gql, {
      input: {
        queryJson: this.safeJSON({
          baseTable: 'BookingPartnerChange',
          baseAlias: 'bookingPartnerChange',
          selects,
          joins: [],
          where,
          having: [],
          sorts: safeSorts,
          groupBy: [],
          skip: (page - 1) * pageSize,
          take: pageSize,
          options: {
            strictMode: options.strict ?? false,
            useCache: options.useCache ?? false
          }
        })
      }
    });

    return this.wrap<BookingPartnerChangePoco[]>(raw, 'listBookingPartnerChange');
  }

  // ============================================================
  // QUERY MODE (GraphQL builder)
  // ============================================================
  async query(
    b: (q: BookingPartnerChangeBuilder) => BookingPartnerChangeBuilder,
    filter: any = {},
    sorts: any[] = [],
    page = 1,
    pageSize = 50,
    options: { strict?: boolean } = {}
  ): Promise<ApiResponse<BookingPartnerChangePoco[]>> {

    const q = new BookingPartnerChangeBuilder();
    b(q);

    const selects = q.getSelections().map(x => ({
      columnName: x,
      alias : x,
      isTrusted: true
    }));

    const gql = `query QBookingPartnerChange($input: DynamicQueryJsonInput) {
      listBookingPartnerChange(input: $input) {
        success
        message
        error
        totalRecord
        data
          ${q.toSelectionSet()}
      }
    }`;

    const raw = await this.api.request<any>(gql, {
      input: {
        queryJson: this.safeJSON({
          baseTable: 'BookingPartnerChange',
          baseAlias: 'bookingPartnerChange',
          selects,
          joins: [],
          where: this.cf(filter),
          having: [],
          sorts,
          groupBy: [],
          skip: (page - 1) * pageSize,
          take: pageSize,
          options: { strictMode: options.strict ?? false }
        })
      }
    });

    return this.wrap<BookingPartnerChangePoco[]>(raw, 'listBookingPartnerChange');
  }

  // ============================================================
  // SQL MODE (SQL AST)
  // ============================================================
  async sql(
    fn: (q: BookingPartnerChangeSql) => void | BookingPartnerChangeSql,
    options: { strict?: boolean, debug?: boolean } = {}
  ): Promise<ApiResponse<BookingPartnerChangePoco[]>> {

    const instance = new BookingPartnerChangeSql();
    const result = fn(instance) || instance;
    const q = result.build();

    q.options = q.options ?? {};
    q.options.strictMode = options.strict ?? false;

    const gql = `query SqlBookingPartnerChange($input: DynamicQueryJsonInput) {
      listBookingPartnerChange(input: $input) {
        success
        message
        error
        totalRecord
        data { id }
      }
    }`;

    const raw = await this.api.request<any>(gql, {
      input: { queryJson: this.safeJSON(q) }
    });

    return this.wrap<BookingPartnerChangePoco[]>(raw, 'listBookingPartnerChange');
  }

  // ============================================================
  // SAVE
  // ============================================================
  async save(item: any): Promise<ApiResponse<BookingPartnerChangePoco>> {
    const gql = `mutation SaveBookingPartnerChange($input: UpsertInputOfBookingPartnerChangePocoInput!) {
      saveBookingPartnerChange(input: $input) {
        success
        message
        error
      }
    }`;

    // UpsertInput requires BOTH item and items (items: [X!]! is non-null).
    // Send an empty items array when saving a single record.
    const raw = await this.api.request<any>(gql, { input: { item, items: [] } });

    return {
      success: raw.saveBookingPartnerChange?.success ?? false,
      message: raw.saveBookingPartnerChange?.message ?? '',
      error: raw.saveBookingPartnerChange?.error ?? '',
      data: item
    };
  }

  // ============================================================
  // DELETE
  // ============================================================
  async delete(id: number, hardDelete=false): Promise<ApiResponse<boolean>> {
    const gql = `mutation DeleteBookingPartnerChange($id: Long!, $hardDelete: Boolean!) {
      deleteBookingPartnerChange(id: $id, hardDelete: $hardDelete) {
        success
        message
        error
      }
    }`;

    const raw = await this.api.request<any>(gql, { id, hardDelete });

    return {
      success: raw.deleteBookingPartnerChange?.success ?? false,
      message: raw.deleteBookingPartnerChange?.message ?? '',
      error: raw.deleteBookingPartnerChange?.error ?? '',
      data: raw.deleteBookingPartnerChange?.success ?? false
    };
  }
}
